/**
 * i18n bootstrap for the Instructor Dashboard.
 *
 * Side effects on import:
 *   - Initializes i18next with EN + AR resources.
 *   - Reads persisted language from localStorage (key: `aitp.lang`),
 *     falls back to browser detection, then to `en`.
 *   - Mirrors the active language onto <html lang> + <html dir> so RTL
 *     styles flip the layout.
 *
 * No <I18nextProvider> wrapper is required; `useTranslation()` picks up
 * the singleton instance via react-i18next.
 */

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import ar from './locales/ar.json';

export const SUPPORTED_LANGUAGES = ['en', 'ar'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const STORAGE_KEY = 'aitp.lang';

function applyDocumentDirection(lng: string): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const isRtl = lng.startsWith('ar') || lng.startsWith('he') || lng.startsWith('fa') || lng.startsWith('ur');
  root.setAttribute('lang', lng);
  root.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
    },
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    nonExplicitSupportedLngs: true,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
    },
    returnNull: false,
  });

applyDocumentDirection(i18n.language || 'en');
i18n.on('languageChanged', applyDocumentDirection);

export default i18n;
